import React, { useEffect, useRef, useCallback, useState } from 'react';
import { createPortal } from 'react-dom';
import { startOfWeek } from 'date-fns';
import { AvailabilityDataProvider, useAvailabilityData } from '../../contexts/AvailabilityDataContext';
import { AvailabilityUIProvider, useAvailabilityUI } from '../../contexts/AvailabilityUIContext';
import { useAvailabilityCRUD } from '../../hooks/useAvailabilityCRUD';
import CalendarGrid from './CalendarGrid';
import MonthCalendarGrid from './MonthCalendarGrid';
import CreateAvailabilityModal from './CreateAvailabilityModal';
import EditAvailabilityModal from './EditAvailabilityModal';
import AvailabilityStats from './AvailabilityStats';
import CalendarNavigation from './CalendarNavigation';
import BreakModal from './BreakModal';
import ClearAvailabilityModal from './ClearAvailabilityModal';
import ErrorBoundary from '../common/ErrorBoundary';
import LoadingSpinner from '../common/LoadingSpinner';
import ErrorMessage from '../common/ErrorMessage';
import { PlusIcon, NoSymbolIcon, TrashIcon, ArrowsPointingOutIcon, ArrowsPointingInIcon } from '@heroicons/react/24/outline';

interface AvailabilityMatrixProps {
  className?: string;
}

// Inner component that uses the context
const AvailabilityMatrixContent: React.FC<AvailabilityMatrixProps> = ({ className = '' }) => {
  const { state: dataState } = useAvailabilityData();
  const { state: uiState, dispatch: uiDispatch } = useAvailabilityUI();
  const { fetchWeeklyAvailability, fetchMonthlyAvailability, startClickCreate, closeAllModals } = useAvailabilityCRUD();

  const { viewMode, currentDate } = uiState.calendar;
  const [isBreakModalOpen, setIsBreakModalOpen] = useState(false);
  const [isClearModalOpen, setIsClearModalOpen] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Lock body scroll while the calendar covers the viewport.
  useEffect(() => {
    if (!isFullscreen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [isFullscreen]);

  // Fetch data on mount and when current date or view mode changes
  const refetchCalendar = useCallback(() => {
    if (viewMode === 'week') {
      fetchWeeklyAvailability();
    } else if (viewMode === 'month') {
      fetchMonthlyAvailability();
    }
  }, [fetchWeeklyAvailability, fetchMonthlyAvailability, viewMode]);

  useEffect(() => {
    refetchCalendar();
  }, [refetchCalendar, currentDate]);

  // Handle day click in month view - navigate to that week
  const handleMonthDayClick = useCallback((date: Date) => {
    // Set the current date to the start of the week containing the clicked day
    const weekStart = startOfWeek(date, { weekStartsOn: 1 });
    uiDispatch({ type: 'SET_CURRENT_DATE', payload: weekStart });
    uiDispatch({ type: 'SET_VIEW_MODE', payload: 'week' });
  }, [uiDispatch]);

  // Handle keyboard shortcuts with stable refs to prevent memory leaks
  const keyPressHandlerRef = useRef<(event: KeyboardEvent) => void>(() => {});
  
  // Update the handler function when dependencies change
  useEffect(() => {
    keyPressHandlerRef.current = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        // Modal escape first; then fullscreen escape so users get a step-back path.
        const modalOpen = uiState.modals.create.isOpen
          || uiState.modals.edit.isOpen
          || uiState.modals.delete.isOpen
          || isBreakModalOpen
          || isClearModalOpen;
        if (modalOpen) {
          closeAllModals();
          setIsBreakModalOpen(false);
          setIsClearModalOpen(false);
        } else if (isFullscreen) {
          setIsFullscreen(false);
        }
      }
      if (event.key === 'n' && (event.ctrlKey || event.metaKey)) {
        event.preventDefault();
        startClickCreate(uiState.calendar.currentDate);
      }
    };
  }, [closeAllModals, startClickCreate, uiState.calendar.currentDate,
      uiState.modals.create.isOpen, uiState.modals.edit.isOpen, uiState.modals.delete.isOpen,
      isBreakModalOpen, isClearModalOpen, isFullscreen]);

  // Add/remove event listener only once
  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      keyPressHandlerRef.current?.(event);
    };

    document.addEventListener('keydown', handleKeyPress);
    return () => document.removeEventListener('keydown', handleKeyPress);
  }, []); // Empty dependency array - listener is added/removed only once

  if (dataState.loading && !dataState.weeklyAvailability) {
    return (
      <div className={`${className} flex items-center justify-center h-64`}>
        <LoadingSpinner size="large" />
      </div>
    );
  }

  // Fullscreen scopes the CALENDAR CARD ONLY — page header/stats stay in normal
  // flow underneath, the card pins to the viewport with `flex flex-col
  // overflow-hidden` so only the grid's internal time-of-day scroll moves.
  // Lock the entity → no competing parent/child scroll on mobile.
  const calendarCardClass = isFullscreen
    ? 'fixed inset-0 z-40 bg-white flex flex-col overflow-hidden'
    : 'bg-white rounded-card shadow-sm border border-clinic-primary-lighter overflow-hidden flex flex-col';

  // In fullscreen the toolbar takes ~64-120px (taller if buttons wrap on mobile);
  // grid fills the rest. In normal flow we keep the existing budget.
  const gridMaxHeight = isFullscreen ? 'calc(100vh - 120px)' : 'calc(70vh - 64px)';

  // The calendar card itself. Built once, rendered either inline (normal) or
  // through a Portal to <body> when fullscreen — Portal guarantees `fixed
  // inset-0` truly pins to the viewport (an ancestor with `transform`/`filter`
  // would otherwise create a containing block and demote it to absolute-within,
  // which is the "page header peeks out" symptom).
  const calendarCard = (
    <div className={calendarCardClass}>
      {/* Toolbar — nav on the left, actions on the right, separator between. Stacks on mobile. */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between px-3 py-2 border-b border-clinic-primary-lighter bg-clinic-cream/30 flex-shrink-0">
        <div className="min-w-0">
          <CalendarNavigation />
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Visual divider so Week/Month toggle doesn't blur into Add Break. */}
          <div className="hidden sm:block h-6 w-px bg-clinic-primary-lighter mr-1" aria-hidden="true" />
          <button
            data-guide="clear-availability"
            onClick={() => setIsClearModalOpen(true)}
            className="inline-flex items-center px-3 py-1.5 border border-clinic-primary-lighter text-sm font-medium rounded-lg text-clinic-dark bg-white hover:bg-clinic-cream transition-colors disabled:opacity-50"
            disabled={dataState.loading}
            title="Remove open availability in bulk (booked time is kept)"
          >
            <TrashIcon className="h-4 w-4 sm:mr-1.5" />
            <span className="hidden sm:inline">Clear Availability</span>
            <span className="sm:hidden ml-1">Clear</span>
          </button>
          <button
            data-guide="add-break"
            onClick={() => setIsBreakModalOpen(true)}
            className="inline-flex items-center px-3 py-1.5 border border-clinic-primary-lighter text-sm font-medium rounded-lg text-clinic-dark bg-white hover:bg-clinic-cream transition-colors disabled:opacity-50"
            disabled={dataState.loading}
          >
            <NoSymbolIcon className="h-4 w-4 sm:mr-1.5" />
            <span className="hidden sm:inline">Add Break</span>
            <span className="sm:hidden ml-1">Break</span>
          </button>
          <button
            data-guide="add-availability"
            onClick={() => startClickCreate(uiState.calendar.currentDate)}
            className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-lg text-white bg-clinic-primary hover:bg-clinic-primary-dark transition-colors disabled:opacity-50"
            disabled={dataState.loading}
          >
            <PlusIcon className="h-4 w-4 sm:mr-1.5" />
            <span className="hidden sm:inline">Add Availability</span>
            <span className="sm:hidden ml-1">Availability</span>
          </button>
          <button
            onClick={() => setIsFullscreen(f => !f)}
            className="inline-flex items-center justify-center p-1.5 border border-clinic-primary-lighter rounded-lg text-clinic-dark bg-white hover:bg-clinic-cream transition-colors"
            title={isFullscreen ? 'Exit fullscreen (Esc)' : 'Expand calendar'}
            aria-label={isFullscreen ? 'Exit fullscreen' : 'Expand calendar'}
          >
            {isFullscreen
              ? <ArrowsPointingInIcon className="h-4 w-4" />
              : <ArrowsPointingOutIcon className="h-4 w-4" />}
          </button>
        </div>
      </div>

      {/* Grid body — fills the rest of the card; only its own internal scroll moves. */}
      <div className="flex-1 min-h-0 overflow-hidden">
        {viewMode === 'week' ? (
          <CalendarGrid maxHeight={gridMaxHeight} />
        ) : (
          <MonthCalendarGrid
            currentDate={currentDate}
            availabilityData={dataState.monthlyAvailability || []}
            onDayClick={handleMonthDayClick}
          />
        )}
      </div>
    </div>
  );

  return (
    <div className={`${className} space-y-6`}>
      {/* Page header — title only; all calendar actions live on the calendar card now. */}
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-clinic-dark">Availability Management</h1>
        {/* Drag/click hints are desktop-only UX — hide on touch where they don't apply. */}
        <p className="text-clinic-muted mt-2 hidden sm:block">
          <strong>Drag</strong> to create new availability • <strong>Click</strong> existing blocks to edit or delete
        </p>
      </div>

      {/* Error Display */}
      {dataState.error && (
        <ErrorMessage
          message={dataState.error}
          onDismiss={() => {/* dispatch SET_ERROR null */}}
        />
      )}

      {/* Stats — hidden in fullscreen so the calendar owns the whole viewport. */}
      {!isFullscreen && (dataState.weeklyAvailability || dataState.monthlyAvailability) && (
        <AvailabilityStats
          availability={dataState.weeklyAvailability}
          monthlyBlocks={dataState.monthlyAvailability}
          viewMode={viewMode}
          currentDate={currentDate}  // M6 fix: pass currentDate for month filtering
        />
      )}

      {/* Calendar Card placeholder — actual card is rendered below (inline or portaled). */}
      {!isFullscreen && calendarCard}

      {/* Modals */}
      {uiState.modals.create.isOpen && (
        <CreateAvailabilityModal />
      )}
      
      {uiState.modals.edit.isOpen && uiState.modals.edit.block && (
        <EditAvailabilityModal />
      )}

      <ClearAvailabilityModal
        isOpen={isClearModalOpen}
        onClose={() => setIsClearModalOpen(false)}
        currentDate={currentDate}
      />

      <BreakModal
        isOpen={isBreakModalOpen}
        onClose={() => setIsBreakModalOpen(false)}
        onChanged={refetchCalendar}
        initialDate={currentDate}
      />

      {/* Loading Overlay */}
      {dataState.loading && dataState.weeklyAvailability && (
        <div className="fixed inset-0 bg-clinic-dark/25 flex items-center justify-center z-50">
          <div className="bg-white rounded-card p-6 shadow-xl border border-clinic-primary-lighter">
            <LoadingSpinner size="medium" />
            <p className="mt-2 text-sm text-clinic-muted">Updating availability...</p>
          </div>
        </div>
      )}

      {/* Fullscreen render: Portal to <body> so no ancestor stacking context can
          push us inside the dashboard chrome (eliminates the "header peeks out" bug). */}
      {isFullscreen && createPortal(calendarCard, document.body)}
    </div>
  );
};

// Main exported component with provider wrapper
const AvailabilityMatrix: React.FC<AvailabilityMatrixProps> = (props) => {
  return (
    <ErrorBoundary>
      <AvailabilityDataProvider>
        <AvailabilityUIProvider>
          <AvailabilityMatrixContent {...props} />
        </AvailabilityUIProvider>
      </AvailabilityDataProvider>
    </ErrorBoundary>
  );
};

export default AvailabilityMatrix;